import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core'
import { MatCardModule } from '@angular/material/card'
import { CommonModule } from '@angular/common'
import { provideNativeDateAdapter } from '@angular/material/core'
import { MatDialog } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { FormsModule } from '@angular/forms'
import { MatInputModule } from '@angular/material/input'
import { MatFormFieldModule } from '@angular/material/form-field'
import { LineupService } from '../../services/lineup.service'
import { Player } from '../../../../core/models/player.model'
import { MatIconModule } from '@angular/material/icon'
import { MatGridListModule } from '@angular/material/grid-list'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { PlayerPriceDialog } from './player-price-dialog/player-price-dialog.component'
import { PlayerService } from '../../../../core/services/player.service'
import { MatSort, MatSortModule } from '@angular/material/sort'
import { MatTooltipModule } from '@angular/material/tooltip'

@Component({
  selector: 'signings-component',
  templateUrl: 'signings.component.html',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatTooltipModule,
    MatTableModule,
    MatIconModule,
    MatGridListModule,
    MatSortModule,
    MatButtonModule,
],
  styleUrls: ['./signings.component.css']
})

export class SigningsComponent implements AfterViewInit {
  constructor(
    public service: LineupService,
    private playerService: PlayerService,
    public dialog: MatDialog,
  ) {}

  @ViewChild('sortVentas') sortVentas!: MatSort
  @ViewChild('sortFichajes') sortFichajes!: MatSort

  currentTeam: Player[] = []
  soldPlayers = new MatTableDataSource<{ player: Player; price: number }>()
  signings = new MatTableDataSource<{ player: Player; positionId: number; price?: number }>()

  totalSold = 0
  totalBought = 0

  ngAfterViewInit() {
    this.playerService.initMarket().subscribe((response) => {
      const {squad, soldPlayers, signedPlayers} = response as {squad: any[], soldPlayers: any[], signedPlayers: any[]}
      this.currentTeam = squad
      .map(e => e.player) 
      .sort((a, b) => {
        const nameA = (a?.commonName || a?.name || '').toLowerCase()
        const nameB = (b?.commonName || b?.name || '').toLowerCase()

        if (!nameA && !nameB) return 0
        if (!nameA) return 1
        if (!nameB) return -1

        return nameA.localeCompare(nameB)
      })

      this.soldPlayers.data = soldPlayers
      this.signings.data = signedPlayers

      this.totalSold = soldPlayers.reduce((sum, p) => sum + (p.price || 0), 0)
      this.totalBought = signedPlayers.reduce((sum, p) => sum + (p.price || 0), 0)
    })

    this.service.playerAddedEvent.subscribe((positionKey) => {
      if (positionKey.team !== 'for') return
      const positionId = positionKey.positionId

      // Obtenemos la posición que coincide
      const position = this.service.getPositions('for').find(
        p => p.starterPositionId === positionId
      )
      if (!position) return

      const player = position.player
      const subPlayer = position.subPlayer

      // Filtramos signings quitando los que tengan el mismo positionId numérico
      let filteredSignings = this.signings.data.filter(
        e => e.positionId !== positionKey.positionId
      )

      const tryAdd = (p: Player | undefined) => {
        if (
          p &&
          p.currentTeam?._id !== '66969b1f21b3dbe997311819' &&
          !filteredSignings.some(s => s.player._id === p._id)
        ) {
          filteredSignings.push({ player: p, positionId: positionKey.positionId })
        }
      }

      tryAdd(player)
      tryAdd(subPlayer)

      this.signings.data = filteredSignings.sort((a, b) =>
        (a.player.commonName ?? a.player.name).localeCompare(b.player.commonName ?? b.player.name)
      )
    })

    this.soldPlayers.sortingDataAccessor = (item, property) => {
      if (property === 'price') return item.price
      if (property === 'players') return item.player.commonName?.toLowerCase() || ''
      return ''
    }

    this.signings.sortingDataAccessor = (item, property) => {
      if (property === 'price') return item.price ?? 0
      if (property === 'players') return item.player.commonName?.toLowerCase() || ''
      return ''
    }

    this.soldPlayers.sort = this.sortVentas
    this.signings.sort = this.sortFichajes
  }

  sellPlayer(player: Player) {
    const dialogRef = this.dialog.open(PlayerPriceDialog, { width: '50vw', data: player })

    dialogRef.afterClosed().subscribe(() => {
      const price = this.service.priceForm.get('price')?.value
      if (price == null || price < 0) return

      this.currentTeam = this.currentTeam.filter((p) => p._id !== player._id)
      this.soldPlayers.data = [...this.soldPlayers.data, { player, price }].sort((a, b) =>
        (a.player.commonName ?? a.player.name).localeCompare(b.player.commonName ?? b.player.name)
      )
      this.totalSold += price
    })
  }

  unsellPlayer(player: Player, price: number) {
    this.soldPlayers.data = this.soldPlayers.data.filter((p) => p.player._id !== player._id)
    this.currentTeam = [...this.currentTeam, player].sort((a, b) => (a.commonName ?? a.name).localeCompare(b.commonName ?? b.name))
    this.totalSold -= price
  }

  buyPrice(playerId?: Player, positionId?: number) {
    this.service.priceForm.reset({player: playerId ?? null, price: null})
    const dialogRef = this.dialog.open(PlayerPriceDialog, { width: '50vw', data: playerId })

    dialogRef.afterClosed().subscribe(() => {
      const player = this.service.priceForm.get('player')?.value
      const price = this.service.priceForm.get('price')?.value
      if (price == null || price < 0) return

      const newSignings = player ? 
        this.signings.data.filter((p) => p.player._id !== player._id) :
        this.signings.data.filter((p) => playerId && p.player._id !== playerId._id)

      
      this.signings.data = !player ? [...newSignings] : [...newSignings, { player, positionId: positionId ?? -1, price }]
      .sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
      
      this.totalBought = 0
      this.signings.data.forEach((s) => (this.totalBought += s.price ?? 0))
    })
  }
}